import React, { useState, useEffect, useContext } from "react";
import { AuthContext, API } from "../App";
import { Card, CardHeader, CardTitle, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "./ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "./ui/alert-dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "./ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Badge } from "./ui/badge";
import { Skeleton } from "./ui/skeleton";
import {
  Plus,
  Building2,
  MapPin,
  Users,
  Calendar,
  Edit,
  Eye,
  MoreVertical,
  Trash2
} from "lucide-react";
import { toast } from "sonner";

export default function SiteManagement() {
  const { user } = useContext(AuthContext);
  const canCreateSites = user?.role === 'admin' || user?.role === 'company_owner';
  const canDeleteSites = user?.role === 'admin' || user?.role === 'company_owner';
  const canEditSites = user?.role === 'admin' || user?.role === 'company_owner' || user?.role === 'site_manager';
  const [sites, setSites] = useState([]);
  const [siteManagers, setSiteManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedSite, setSelectedSite] = useState(null);
  
  // Add new site manager dialog
  const [showAddManagerDialog, setShowAddManagerDialog] = useState(false);
  const [newManager, setNewManager] = useState({
    username: "",
    email: "",
    password: "",
    role: "site_manager"
  });
  const [addingManager, setAddingManager] = useState(false);

  const [newSite, setNewSite] = useState({
    name: "",
    address: "",
    description: "",
    site_manager_ids: [],  // Array for multiple managers
    client_name: "",
    client_email: "",
    client_phone: "",
    client_notes: ""
  });

  const [editSite, setEditSite] = useState({
    name: "",
    address: "",
    description: "",
    site_manager_ids: [],  // Array for multiple managers
    client_name: "",
    client_email: "",
    client_phone: "",
    client_notes: ""
  });

  useEffect(() => {
    loadSites();
    loadSiteManagers();
  }, []);

  const loadSites = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API}/sites`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSites(data);
      } else {
        toast.error("Failed to load sites");
      }
    } catch (error) {
      console.error("Error loading sites:", error);
      toast.error("Failed to load sites");
    } finally {
      setLoading(false);
    }
  };

  const loadSiteManagers = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API}/users/site-managers`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSiteManagers(data);
      }
    } catch (error) {
      console.error("Error loading site managers:", error);
      // Don't show error for this as it's not critical
    }
  };

  const handleAddSiteManager = async () => {
    if (!newManager.username.trim() || !newManager.email.trim() || !newManager.password.trim()) {
      toast.error("All fields are required");
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newManager.email)) {
      toast.error("Please enter a valid email address");
      return;
    }

    setAddingManager(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API}/auth/register`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          username: newManager.username,
          email: newManager.email,
          password: newManager.password,
          role: newManager.role
        })
      });

      if (response.ok) {
        const createdManager = await response.json();
        setSiteManagers(prev => [...prev, createdManager.user]);
        toast.success(`User "${newManager.username}" added successfully!`);
        setNewManager({ username: "", email: "", password: "", role: "site_manager" });
        setShowAddManagerDialog(false);
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || "Failed to add site manager");
      }
    } catch (error) {
      console.error("Error adding site manager:", error);
      toast.error("Failed to add site manager");
    } finally {
      setAddingManager(false);
    }
  };

  const handleCreateSite = async (e) => {
    e.preventDefault();
    
    if (!newSite.name.trim() || !newSite.address.trim()) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API}/sites`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(newSite)
      });

      if (response.ok) {
        toast.success("Site created successfully!");
        setShowCreateDialog(false);
        setNewSite({ name: "", address: "", description: "", site_manager_ids: [], client_name: "", client_email: "", client_phone: "", client_notes: "" });
        loadSites();
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || "Failed to create site");
      }
    } catch (error) {
      console.error("Error creating site:", error);
      toast.error("Failed to create site");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditSite = (site) => {
    setSelectedSite(site);
    setEditSite({
      name: site.name,
      address: site.address,
      description: site.description || "",
      site_manager_ids: site.site_manager_ids || [],
      client_name: site.client_name || "",
      client_email: site.client_email || "",
      client_phone: site.client_phone || "",
      client_notes: site.client_notes || ""
    });
    setShowEditDialog(true);
  };

  const handleUpdateSite = async (e) => {
    e.preventDefault();
    
    if (!editSite.name.trim() || !editSite.address.trim()) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API}/sites/${selectedSite.id}`, {
        method: "PUT",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(editSite)
      });

      if (response.ok) {
        toast.success("Site updated successfully!");
        setShowEditDialog(false);
        setSelectedSite(null);
        setEditSite({ name: "", address: "", description: "", site_manager_ids: [], client_name: "", client_email: "", client_phone: "", client_notes: "" });
        loadSites();
      } else{
        const errorData = await response.json();
        toast.error(errorData.detail || "Failed to update site");
      }
    } catch (error) {
      console.error("Error updating site:", error);
      toast.error("Failed to update site");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteSite = (site) => {
    setSelectedSite(site);
    setShowDeleteDialog(true);
  };

  const confirmDeleteSite = async () => {
    if (!selectedSite) return;

    setIsSubmitting(true);

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API}/sites/${selectedSite.id}`, {
        method: "DELETE",
        headers: {
          "Authorization": `Bearer ${token}`
        }
      });

      if (response.ok) {
        toast.success("Site deleted successfully!");
        setShowDeleteDialog(false);
        setSelectedSite(null);
        loadSites();
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || "Failed to delete site");
      }
    } catch (error) {
      console.error("Error deleting site:", error);
      toast.error("Failed to delete site");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="sites-loading">
        <div className="space-y-6">
          <Skeleton className="h-8 w-64" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <Skeleton key={i} className="h-48" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 fade-in-up" data-testid="site-management-container">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="heading-primary text-4xl mb-2">Site Management</h1>
          <p className="text-lg text-slate-600">Manage construction sites and locations</p>
        </div>
        
        {user?.is_demo ? (
          <Button
            variant="outline"
            className="border-amber-400 text-amber-700"
            onClick={() => toast.info("Demo accounts are limited to one site. Subscribe to add more.")}
            data-testid="demo-upgrade-sites-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Upgrade to add more sites →
          </Button>
        ) : canCreateSites && (
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <Button className="btn-construction" data-testid="add-site-button">
                <Plus className="w-4 h-4 mr-2" />
                Add New Site
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto" data-testid="create-site-dialog">
              <DialogHeader>
                <DialogTitle className="heading-secondary">Create New Site</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateSite} className="space-y-6">
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="site-name" className="font-medium">Site Name *</Label>
                    <Input
                      id="site-name"
                      placeholder="e.g., Downtown Office Complex"
                      value={newSite.name}
                      onChange={(e) => setNewSite(prev => ({ ...prev, name: e.target.value }))}
                      required
                      data-testid="site-name-input"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="site-address" className="font-medium">Address *</Label>
                    <Input
                      id="site-address"
                      placeholder="Full street address"
                      value={newSite.address}
                      onChange={(e) => setNewSite(prev => ({ ...prev, address: e.target.value }))}
                      required
                      data-testid="site-address-input"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="site-description" className="font-medium">Description</Label>
                    <Textarea
                      id="site-description"
                      placeholder="Brief description of the construction project..."
                      value={newSite.description}
                      onChange={(e) => setNewSite(prev => ({ ...prev, description: e.target.value }))}
                      rows={3}
                      data-testid="site-description-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="site-managers" className="font-medium">Site Managers</Label>
                      <Button
                        type="button"
                        variant="link"
                        size="sm"
                        className="h-auto p-0 text-blue-600"
                        onClick={() => setShowAddManagerDialog(true)}
                      >
                        + Add New Manager
                      </Button>
                    </div>
                    <div className="border rounded-lg p-3 max-h-48 overflow-y-auto space-y-2">
                      {siteManagers.length === 0 ? (
                        <p className="text-sm text-gray-500">No site managers available</p>
                      ) : (
                        siteManagers.map((manager) => (
                          <div key={manager.id} className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              id={`new-manager-${manager.id}`}
                              checked={newSite.site_manager_ids.includes(manager.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setNewSite(prev => ({
                                    ...prev,
                                    site_manager_ids: [...prev.site_manager_ids, manager.id]
                                  }));
                                } else {
                                  setNewSite(prev => ({
                                    ...prev,
                                    site_manager_ids: prev.site_manager_ids.filter(id => id !== manager.id)
                                  }));
                                }
                              }}
                              className="w-4 h-4"
                            />
                            <Label
                              htmlFor={`new-manager-${manager.id}`}
                              className="text-sm font-normal cursor-pointer flex-1"
                            >
                              {manager.username} <span className="text-gray-500">({manager.role})</span>
                            </Label>
                          </div>
                        ))
                      )}
                    </div>
                    <p className="text-xs text-gray-500">
                      Selected: {newSite.site_manager_ids.length} manager(s)
                    </p>
                  </div>

                  <div className="space-y-3 border-t pt-4">
                    <div>
                      <Label className="font-medium">Client Details</Label>
                      <p className="text-xs text-gray-500">Optional — used later in Client Comms for communications/variations.</p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="new-client-name" className="text-sm">Client Name</Label>
                      <Input id="new-client-name" placeholder="Client / owner name"
                        value={newSite.client_name}
                        onChange={(e) => setNewSite(prev => ({ ...prev, client_name: e.target.value }))}
                        data-testid="site-client-name-input" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label htmlFor="new-client-email" className="text-sm">Client Email</Label>
                        <Input id="new-client-email" type="email" placeholder="email@example.com"
                          value={newSite.client_email}
                          onChange={(e) => setNewSite(prev => ({ ...prev, client_email: e.target.value }))}
                          data-testid="site-client-email-input" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="new-client-phone" className="text-sm">Client Phone</Label>
                        <Input id="new-client-phone" placeholder="04xx xxx xxx"
                          value={newSite.client_phone}
                          onChange={(e) => setNewSite(prev => ({ ...prev, client_phone: e.target.value }))}
                          data-testid="site-client-phone-input" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="new-client-notes" className="text-sm">Notes</Label>
                      <Textarea id="new-client-notes" rows={2} placeholder="Preferred contact method, etc."
                        value={newSite.client_notes}
                        onChange={(e) => setNewSite(prev => ({ ...prev, client_notes: e.target.value }))}
                        data-testid="site-client-notes-input" />
                    </div>
                  </div>
                </div>

                <div className="flex justify-end space-x-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowCreateDialog(false)}
                    data-testid="cancel-site-button"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="btn-construction"
                    disabled={isSubmitting}
                    data-testid="save-site-button"
                  >
                    {isSubmitting ? (
                      <div className="flex items-center space-x-2">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>Creating...</span>
                      </div>
                    ) : (
                      "Create Site"
                    )}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Sites Grid */}
      {sites.length === 0 ? (
        <Card className="dashboard-card" data-testid="no-sites-card">
          <CardContent className="p-12 text-center">
            <Building2 className="w-16 h-16 text-slate-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-900 mb-2">No Sites Found</h3>
            <p className="text-slate-600 mb-6">Get started by creating your first construction site</p>
            {canCreateSites && (
              <Button
                onClick={() => setShowCreateDialog(true)}
                className="btn-construction"
                data-testid="create-first-site-button"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create First Site
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card className="dashboard-card">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-100 border-b-2 border-slate-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Site Name</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Address</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Managers</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Created</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-slate-700">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {[...sites].sort((a, b) => a.name.localeCompare(b.name)).map((site) => (
                    <tr key={site.id} className="hover:bg-slate-50 transition-colors" data-testid={`site-row-${site.id}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center space-x-2">
                          <Building2 className="w-4 h-4 text-amber-500" />
                          <span className="text-sm font-medium text-slate-900">{site.name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600 max-w-xs truncate">
                        {site.address}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">
                        {site.site_manager_names && site.site_manager_names.length > 0 
                          ? site.site_manager_names.join(", ") 
                          : '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">
                        {new Date(site.created_at).toLocaleDateString('en-US', { 
                          year: 'numeric', 
                          month: 'short', 
                          day: 'numeric' 
                        })}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" data-testid={`site-menu-${site.id}`}>
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleEditSite(site)} data-testid={`view-site-${site.id}`}>
                              <Eye className="w-4 h-4 mr-2" />
                              View Details
                            </DropdownMenuItem>
                            {canEditSites && (
                              <>
                                <DropdownMenuItem onClick={() => handleEditSite(site)} data-testid={`edit-site-${site.id}`}>
                                  <Edit className="w-4 h-4 mr-2" />
                                  Edit Site
                                </DropdownMenuItem>
                                {canDeleteSites && (
                                  <>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem 
                                      onClick={() => handleDeleteSite(site)} 
                                      className="text-red-600 focus:text-red-600"
                                      data-testid={`delete-site-${site.id}`}
                                    >
                                      <Trash2 className="w-4 h-4 mr-2" />
                                      Delete Site
                                    </DropdownMenuItem>
                                  </>
                                )}
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Summary Stats */}
      {sites.length > 0 && (
        <Card className="dashboard-card mt-8" data-testid="sites-summary-card">
          <CardHeader>
            <CardTitle className="heading-secondary">Sites Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-slate-900 mb-2">{sites.length}</div>
                <p className="text-slate-600">Total Sites</p>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-slate-900 mb-2">
                  {user?.assigned_sites?.length || 0}
                </div>
                <p className="text-slate-600">Your Assigned Sites</p>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-slate-900 mb-2">{sites.length}</div>
                <p className="text-slate-600">Active Projects</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Edit Site Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto" data-testid="edit-site-dialog">
          <DialogHeader>
            <DialogTitle className="heading-secondary">Edit Site</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdateSite} className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="edit-site-name" className="font-medium">Site Name *</Label>
                <Input
                  id="edit-site-name"
                  placeholder="e.g., Downtown Office Complex"
                  value={editSite.name}
                  onChange={(e) => setEditSite(prev => ({ ...prev, name: e.target.value }))}
                  required
                  data-testid="edit-site-name-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="edit-site-address" className="font-medium">Address *</Label>
                <Input
                  id="edit-site-address"
                  placeholder="Full street address"
                  value={editSite.address}
                  onChange={(e) => setEditSite(prev => ({ ...prev, address: e.target.value }))}
                  required
                  data-testid="edit-site-address-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="edit-site-description" className="font-medium">Description</Label>
                <Textarea
                  id="edit-site-description"
                  placeholder="Brief description of the construction project..."
                  value={editSite.description}
                  onChange={(e) => setEditSite(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  data-testid="edit-site-description-input"
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="edit-site-managers" className="font-medium">Site Managers</Label>
                  <Button
                    type="button"
                    variant="link"
                    size="sm"
                    className="h-auto p-0 text-blue-600"
                    onClick={() => setShowAddManagerDialog(true)}
                  >
                    + Add New Manager
                  </Button>
                </div>
                <div className="border rounded-lg p-3 max-h-48 overflow-y-auto space-y-2">
                  {siteManagers.length === 0 ? (
                    <p className="text-sm text-gray-500">No site managers available</p>
                  ) : (
                    siteManagers.map((manager) => (
                      <div key={manager.id} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id={`edit-manager-${manager.id}`}
                          checked={editSite.site_manager_ids.includes(manager.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setEditSite(prev => ({
                                ...prev,
                                site_manager_ids: [...prev.site_manager_ids, manager.id]
                              }));
                            } else {
                              setEditSite(prev => ({
                                ...prev,
                                site_manager_ids: prev.site_manager_ids.filter(id => id !== manager.id)
                              }));
                            }
                          }}
                          className="w-4 h-4"
                        />
                        <Label
                          htmlFor={`edit-manager-${manager.id}`}
                          className="text-sm font-normal cursor-pointer flex-1"
                        >
                          {manager.username} <span className="text-gray-500">({manager.role})</span>
                        </Label>
                      </div>
                    ))
                  )}
                </div>
                <p className="text-xs text-gray-500">
                  Selected: {editSite.site_manager_ids.length} manager(s)
                </p>
              </div>

              <div className="space-y-3 border-t pt-4">
                <div>
                  <Label className="font-medium">Client Details</Label>
                  <p className="text-xs text-gray-500">Used in Client Comms for communications/variations.</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-client-name" className="text-sm">Client Name</Label>
                  <Input id="edit-client-name" placeholder="Client / owner name"
                    value={editSite.client_name}
                    onChange={(e) => setEditSite(prev => ({ ...prev, client_name: e.target.value }))}
                    data-testid="edit-site-client-name-input" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="edit-client-email" className="text-sm">Client Email</Label>
                    <Input id="edit-client-email" type="email" placeholder="email@example.com"
                      value={editSite.client_email}
                      onChange={(e) => setEditSite(prev => ({ ...prev, client_email: e.target.value }))}
                      data-testid="edit-site-client-email-input" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-client-phone" className="text-sm">Client Phone</Label>
                    <Input id="edit-client-phone" placeholder="04xx xxx xxx"
                      value={editSite.client_phone}
                      onChange={(e) => setEditSite(prev => ({ ...prev, client_phone: e.target.value }))}
                      data-testid="edit-site-client-phone-input" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-client-notes" className="text-sm">Notes</Label>
                  <Textarea id="edit-client-notes" rows={2} placeholder="Preferred contact method, etc."
                    value={editSite.client_notes}
                    onChange={(e) => setEditSite(prev => ({ ...prev, client_notes: e.target.value }))}
                    data-testid="edit-site-client-notes-input" />
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowEditDialog(false)}
                data-testid="cancel-edit-site-button"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="btn-construction"
                disabled={isSubmitting}
                data-testid="update-site-button"
              >
                {isSubmitting ? (
                  <div className="flex items-center space-x-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Updating...</span>
                  </div>
                ) : (
                  "Update Site"
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent data-testid="delete-site-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-600">Delete Site</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{selectedSite?.name}</strong>? 
              This action cannot be undone and will remove all associated data.
              {selectedSite && (
                <div className="mt-3 p-3 bg-red-50 rounded-lg border border-red-200">
                  <p className="text-sm text-red-800">
                    <strong>Warning:</strong> This site cannot be deleted if it has existing daily records.
                  </p>
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-site-button">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteSite}
              className="bg-red-600 hover:bg-red-700 text-white"
              disabled={isSubmitting}
              data-testid="confirm-delete-site-button"
            >
              {isSubmitting ? (
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Deleting...</span>
                </div>
              ) : (
                "Delete Site"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Add Site Manager Dialog */}
      <Dialog open={showAddManagerDialog} onOpenChange={setShowAddManagerDialog}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold">Add New User</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="new-manager-name" className="text-sm font-medium">
                Full Name *
              </Label>
              <Input
                id="new-manager-name"
                placeholder="e.g., John Smith"
                value={newManager.username}
                onChange={(e) => setNewManager(prev => ({ ...prev, username: e.target.value }))}
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-manager-email" className="text-sm font-medium">
                Email Address *
              </Label>
              <Input
                id="new-manager-email"
                type="email"
                placeholder="e.g., john@construction.com"
                value={newManager.email}
                onChange={(e) => setNewManager(prev => ({ ...prev, email: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-manager-password" className="text-sm font-medium">
                Password *
              </Label>
              <Input
                id="new-manager-password"
                type="password"
                placeholder="Minimum 6 characters"
                value={newManager.password}
                onChange={(e) => setNewManager(prev => ({ ...prev, password: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-manager-role" className="text-sm font-medium">
                User Role *
              </Label>
              <select
                id="new-manager-role"
                value={newManager.role}
                onChange={(e) => setNewManager(prev => ({ ...prev, role: e.target.value }))}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                <option value="admin">Admin - Full system access</option>
                <option value="site_manager">Site Manager - Manage assigned sites</option>
                <option value="view_only">View Only - Read-only access to all data</option>
                <option value="supervisor">Supervisor - View assigned sites only</option>
              </select>
            </div>
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-xs text-blue-800">
                <strong>Note:</strong> The new user will be created with the selected role. Admin and View Only users can see all sites. Site Managers and Supervisors see only assigned sites.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowAddManagerDialog(false);
                setNewManager({ username: "", email: "", password: "", role: "site_manager" });
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleAddSiteManager}
              disabled={addingManager || !newManager.username.trim() || !newManager.email.trim() || !newManager.password.trim()}
              className="bg-slate-700 hover:bg-slate-800"
            >
              {addingManager ? "Adding..." : "Add User"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}